<template>
  <n-card class="intervention-card" :class="{ clickable: isClickable, 'challenging-intervention': isChallenging }">
    <v-row no-gutters class="intervention-header">
      <div class="header-left" v-if="!isChallenging">
        <div class="id">#{{ intervention.displayNum }}</div>
        <div class="status ellipsis" :style="{ 'background-color' : intervention.status.color, color: statusFontColor(intervention.status) }">
          {{ intervention.status.name }}
        </div>
      </div>
      <v-col class="d-flex flex-column header-right">
        <div>
          <div class="description">
            <v-clamp autoresize :max-lines="2">
              {{ intervention.title }}
            </v-clamp>
          </div>
        </div>
        <v-spacer></v-spacer>
        <div>
          <v-row no-gutters>
            <v-col class="site ellipsis mr-2">
              <v-icon small>business</v-icon>
              {{ intervention.site.$label }}
            </v-col>
            <div class="address" v-if="intervention.$address.length">
              {{ intervention.$address }}
              <v-icon small>place</v-icon>
            </div>
          </v-row>
        </div>
      </v-col>
    </v-row>
    <v-row no-gutters class="intervention-footer" v-if="isChallenging">
      <v-col class="footer-item client">
        <v-alert color="primary" text dense class="px-3 mr-3 mb-0 text-center">
          <v-icon style="color: inherit;" class="mr-2">hourglass_bottom</v-icon>
          Mise en concurrence en cours
        </v-alert>
      </v-col>
      <v-col class="footer-item updated-at">
        <div class="footer-item-title ellipsis">
          créée le
        </div>
        <div class="footer-item-content">
          {{ intervention.$createdAt }}
        </div>
      </v-col>
    </v-row>
    <v-row no-gutters class="intervention-footer" v-else>
      <v-col class="footer-item client">
        <template v-if="company.isRoot">
          <div class="footer-item-title ellipsis">client</div>
          <div class="footer-item-content ellipsis">{{ intervention.client.name }}</div>
        </template>
        <template v-else>
          <div class="footer-item-title ellipsis">prestataire</div>
          <div class="footer-item-content ellipsis">{{ intervention.maker.name }}</div>
        </template>
      </v-col>
      <v-col class="footer-item updated-at">
        <div>
          <div class="footer-item-title ellipsis">
            <template v-if="hasBeenUpdated">
              modifiée le
            </template>
            <template v-else>
              créée le
            </template>
          </div>
          <div class="footer-item-content ellipsis">
            <v-tooltip top open-delay="300" color="primary darken-1">
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ intervention.$updatedAt }}
                </span>
              </template>
              <div>
                <div class="text-right">Créée le {{ intervention.$createdAt }}</div>
                <div class="text-right" v-if="hasBeenUpdated">Modifiée le {{ intervention.$updatedAt }}</div>
              </div>
            </v-tooltip>
          </div>
        </div>
      </v-col>
      <v-col class="footer-item assignee">
        <div>
          <div class="footer-item-title ellipsis">assignée à</div>
          <div class="footer-item-content ellipsis">
            {{ hasAssignee ? `${intervention.assignee.firstname} ${intervention.assignee.lastname}` : `-` }}
          </div>
        </div>
      </v-col>
      <v-col class="footer-item date">
        <div>
          <div class="footer-item-title ellipsis">date d'intervention</div>
          <div class="footer-item-content ellipsis">
            <v-tooltip top open-delay="300" color="primary darken-1" v-if="intervention.plannedStartAt">
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ intervention.plannedStartAt | formatDate | defaultInterventionDateLabel }}
                </span>
              </template>
              <div>
                {{ intervention | formatInterventionDate }}
              </div>
            </v-tooltip>
            <template v-else>-</template>
          </div>
        </div>
      </v-col>
      <v-col class="footer-item domain">
        <div>
          <div class="footer-item-title ellipsis">domaine</div>
          <div class="footer-item-content ellipsis">{{ intervention.domain.name }}</div>
        </div>
      </v-col>
      <v-col class="footer-item priority">
        <div>
          <div class="footer-item-title ellipsis">priorité</div>
          <div class="footer-item-content ellipsis">{{ intervention.priority.name }}</div>
        </div>
      </v-col>
      <v-col class="footer-item indicators-wrapper align-self-stretch">
        <div class="indicators">
          <v-row no-gutters align="center" justify="center">
            <div class="indicator-item">
              <v-icon>attach_file</v-icon>
              <div class="indicator-number">{{ intervention.nbFiles }}</div>
            </div>
            <div class="indicator-item">
              <v-icon>chat_bubble_outline</v-icon>
              <div class="indicator-number">{{ intervention.nbComments }}</div>
            </div>
          </v-row>
        </div>
      </v-col>
    </v-row>
    <v-row no-gutters justify="end" align="center" class="px-2 py-1 intervention-tags" v-if="showClientTags">
      <v-menu
        v-model="isShowingTagsList"
        min-width="350"
        :close-on-content-click="false"
        offset-y
        bottom
        v-if="canAddClientTag"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-chip
            color="grey"
            small
            outlined
            class="my-1 mx-2"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon small left class="mr-1">add</v-icon>
            Ajouter un tag
          </v-chip>
        </template>
        <tags-menu-list
          :client-id="intervention.Client_id"
          :excluded-tags="intervention.clientTags"
          @selected="clientTagSelected"
          v-if="isShowingTagsList"
        />
      </v-menu>
      <div>
        <tag-chip
          v-for="tag in intervention.clientTags"
          :key="`tag-${tag.id}`"
          :tag="tag"
          :removable="canRemoveClientTag"
          @remove="removeClientTag(tag)"
        />
      </div>
    </v-row>
    <v-row no-gutters justify="end" align="center" class="px-2 py-1 intervention-tags" v-else-if="showMakerTags">
      <v-menu
        v-model="isShowingTagsList"
        min-width="350"
        :close-on-content-click="false"
        offset-y
        bottom
        v-if="canAddMakerTag"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-chip
            color="grey"
            small
            outlined
            class="my-1 mx-2"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon small left class="mr-1">add</v-icon>
            Ajouter un tag
          </v-chip>
        </template>
        <tags-menu-list
          :maker-id="intervention.Maker_id"
          :excluded-tags="intervention.makerTags"
          @selected="makerTagSelected"
          v-if="isShowingTagsList"
        />
      </v-menu>
      <div>
        <tag-chip
          v-for="tag in intervention.makerTags"
          :key="`tag-${tag.id}`"
          :tag="tag"
          :removable="canRemoveMakerTag"
          @remove="removeMakerTag(tag)"
        />
      </div>
    </v-row>
  </n-card>
</template>

<script>
import moment from 'moment';
import _ from 'lodash';
import { mapGetters, mapState } from 'vuex';
import TagsMenuList from '@/components/Tags/MenuList.vue';

moment.locale(`fr`);

export default {
  name: `InterventionCard`,

  props: {
    value: Object,
    clickable: Boolean,
    tagAdmin: Boolean,
    isChallenging: Boolean,
  },

  data: () => ({
    isShowingTagsList: false,
  }),

  computed: {
    ...mapState(`model/client`, [
      `company`,
      `isMaker`,
    ]),
    ...mapState(`model/user`, [
      `me`,
      `isMakerUser`,
      `isAggregatorUser`,
      `isAtLeastContributor`,
    ]),
    isClickable() {
      return this.clickable === true;
    },
    canAddClientTag() {
      return this.tagAdmin === true && this.isAggregatorUser && this.isAtLeastContributor;
    },
    canAddMakerTag() {
      return this.tagAdmin === true && this.isMakerUser && this.isMaker && this.isAtLeastContributor;
    },
    canRemoveClientTag() {
      return this.canAddClientTag;
    },
    canRemoveMakerTag() {
      return this.canAddMakerTag;
    },
    intervention() {
      const intervention = this.value;
      const { site } = intervention;
      const $createdAt = moment(intervention.createdAt).format(`DD/MM/YYYY [à] HH[h]mm`);
      const $updatedAt = moment(intervention.updatedAt).format(`DD/MM/YYYY [à] HH[h]mm`);
      const $refLabel = site.ref && site.ref.length ? ` (Ref: ${site.ref})` : ``;

      return {
        ...intervention,
        $createdAt,
        $updatedAt,
        $address: _.trim(`${site.address || ``} ${site.postalCode || ``} ${site.city || ``}`).replace(/ +/g, ` `),
        site: {
          ...site,
          $label: `${site.name}${$refLabel}`,
        },
      };
    },
    hasTags() {
      if (this.isAggregatorUser) {
        return Boolean((this.intervention.clientTags || []).length);
      }

      if (this.isMaker && this.isMakerUser) {
        return Boolean((this.intervention.makerTags || []).length);
      }

      return false;
    },
    showClientTags() {
      return this.isAggregatorUser && ((this.tagAdmin && this.canAddClientTag) || this.hasTags);
    },
    showMakerTags() {
      return this.isMakerUser && this.isMaker && ((this.tagAdmin && this.canAddMakerTag) || this.hasTags);
    },
    hasBeenUpdated() {
      return this.intervention.createdAt !== this.intervention.updatedAt;
    },
    hasAssignee() {
      return this.intervention.assignee && this.intervention.assignee.id;
    },
    ...mapGetters(`model/intervention`, [
      `statusFontColor`,
      `colorFromBG`,
    ]),
  },

  methods: {
    async clientTagSelected(tag) {
      this.isShowingTagsList = false;
      await this.$store.dispatch(`ui/intervention/addClientTag`, tag);
      this.$emit(`tag-added`, tag);
    },
    async makerTagSelected(tag) {
      this.isShowingTagsList = false;
      await this.$store.dispatch(`ui/intervention/addMakerTag`, tag);
      this.$emit(`tag-added`, tag);
    },
    async removeClientTag(tag) {
      await this.$store.dispatch(`ui/intervention/removeClientTag`, tag);
      this.$emit(`tag-removed`, tag);
    },
    async removeMakerTag(tag) {
      await this.$store.dispatch(`ui/intervention/removeMakerTag`, tag);
      this.$emit(`tag-removed`, tag);
    },
  },

  components: {
    TagsMenuList,
  },
};
</script>

<style lang="scss">
.intervention-card {
  padding-top: 10px;
  .intervention-header {
    padding-bottom: 4px;
    border-bottom: 1px solid #E2E2E2;
  }
  .header-left {
    width: 160px;
    padding-top: 6px;
    margin-right: 10px;
    .id,
    .status {
      text-align: center;
    }
    .id {
      font-size: 0.9rem;
      font-weight: 500;
    }
    .status {
      background-color: #06d006;
      color: #FFF;
      border-radius: 0px 15px 15px 0px;
      font-size: 0.8rem;
      font-weight: 500;
      margin-top: 6px;
      padding: 5px 6px;
    }
  }
  .header-right {
    margin-right: 15px;
    min-height: 72px;
    .description {
      margin-right: 40px;
      text-align: left;
      font-size: 1.1rem;
      font-weight: 700;
      line-height: 1.5rem;
    }
    .more {
      margin-top: -5px;
    }
    .address,
    .site {
      font-size: 0.8rem;
      text-align: left;
      .v-icon {
        color: $primaryColor;
        margin-top: -4px;
        margin-right: 0px;
      }
    }
    .address .v-icon {
      color: $secondaryColor;
    }
    .time-infos {
      font-style: italic;
      text-align: right;
      font-size: 0.8rem;
      padding-right: 3px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &.challenging-intervention .header-right {
    margin-left: 10px;
  }

  .intervention-footer {
    margin: 0px 1px 1px;
  }

  .footer-item {
    min-width: 0;
    text-align: left;
    margin: 0px;
    background-color: transparent;
    &, &.col {
      padding: 8px;
    }
    &:not(:first-of-type) {
      border-left: 1px solid #E2E2E2;
    }
    .footer-item-title {
      font-size: 0.8rem;
      color: #888;
    }
    .footer-item-content {
      font-weight: 500;
      color: #111;
    }
    &.updated-at {
      min-width: 172px;
    }
    &.date {
      max-width: 130px;
    }
    &.indicators-wrapper {
      max-width: 120px;
      .indicators {
        height: 100%;
        & > .row {
          height: 100%;
        }
      }
      .indicator-item {
        &:not(:last-child) {
          margin-right: 14px;
        }
        .v-icon {
          margin-top: -1px;
          font-size: 1rem;
          margin-right: 3px;
          opacity: .6;
        }
      }
      .indicator-number {
        display: inline-block;
        font-size: 1rem;
        font-weight: 500;
        color: #111;
      }
    }
  }

  .intervention-tags {
    border-top: 1px solid #E2E2E2;
  }
}
</style>
